/**
 * Action Type Registry — sync lookups backed by in-memory cache
 *
 * The database is the sole source of truth for action types. This module
 * provides synchronous lookup helpers that read from an in-memory cache.
 * The cache is populated on both runtimes: on the backend by
 * action-type-cache.ts (direct DB read), and in the browser by
 * web/app-home/src/view-system/initActionTypes.ts (fetches
 * GET /api/v4/action-types, invoked app-wide in AppRouter).
 *
 * The store starts empty and fills *asynchronously* after that fetch, so
 * lookups return undefined during the cold window (before init lands, or if
 * it fails). Callers must tolerate this with a fallback — it is a transient
 * cold window, NOT a permanently-cold frontend.
 */

import type { ActionTypeEntry } from './core';

// ── In-memory store (populated by action-type-cache.ts on backend) ──

let entries: ActionTypeEntry[] = [];

/** Replace the in-memory action type entries. Called by action-type-cache.ts after DB fetch. */
export function setActionTypeEntries(newEntries: ActionTypeEntry[]): void {
  entries = newEntries;
}

// ── Public API ───────────────────────────────────────────────

/** Wide type that accepts both built-in and custom (database-backed) action type slugs. */
export type ActionType = string;

/** Synchronous lookup by slug. Returns undefined if not found or cache is cold. */
export function getActionTypeEntry(id: string): ActionTypeEntry | undefined {
  return entries.find((e) => e.id === id);
}

/** Get all cached entries synchronously. Returns empty array if cache is cold. */
export function getActionTypes(): ActionTypeEntry[] {
  return entries;
}
